/** Scoring a finished game, and the table it goes on.
 *
 * A score is a *host* fact, which is why none of this is in the kernel. Two of
 * the three things that decide it are not in a `GameState` at all - how hard
 * the computer was set to play, and what the player asked for when he started -
 * and the third, what the kingdom was worth, is a kernel function the host
 * calls rather than re-implements (`kingdomScore`, adventure/turn.ts). Scoring
 * in the kernel would put a number nothing simulates into every save.
 *
 * The arithmetic itself is in `@game/ui`'s `scoreModel`, beside the screen that
 * prints it: this file is the two things the screen cannot do, which are asking
 * the kernel what a kingdom is worth and finding the table in local storage.
 */

import type { ContentRegistry, GameState, PlayerId } from "@game/core";
import { absoluteDay, kingdomScore, makeCtx } from "@game/core";
import type { HighScoreRow, ScoreOutcome } from "@game/ui";
import { finalScore as scoreOf } from "@game/ui";

import type { GameOptions } from "./newgame.js";
import { difficultyIdFor } from "./controllers.js";

const TABLE_KEY = "realm.scores.v1";
/** Rows kept. Enough that a good game stays on the board for a week of play. */
export const MAX_SCORES = 20;

export function outcomeFor(state: GameState, viewer: PlayerId): ScoreOutcome {
  const w = state.victory.winner;
  if (w === null) return "draw";
  return w === viewer ? "victory" : "defeat";
}

/** The score for one finished game. The formula is `scoreBreakdown`. */
export function finalScore(
  state: GameState, content: ContentRegistry, viewer: PlayerId, difficulty: number,
): number {
  return scoreOf({
    kingdom: kingdomScore(makeCtx(state, content), viewer),
    day: absoluteDay(state),
    difficulty: difficultyIdFor(difficulty),
    outcome: outcomeFor(state, viewer),
  });
}

/** Everything the table shows, built from the game that has just ended. */
export function scoreRow(
  state: GameState, content: ContentRegistry, viewer: PlayerId, options: GameOptions,
): HighScoreRow {
  const p = state.players[viewer];
  return {
    at: Date.now(),
    score: finalScore(state, content, viewer, options.difficulty),
    outcome: outcomeFor(state, viewer),
    colour: p?.colour ?? "red",
    faction: (p?.faction as string | undefined) ?? options.faction,
    difficulty: difficultyIdFor(options.difficulty),
    day: absoluteDay(state),
    mapCells: options.mapCells,
    opponents: options.opponents,
    condition: state.victory.condition,
  };
}

// ------------------------------------------------------------------ storage

function storage(): Storage | null {
  try {
    const s = globalThis.localStorage;
    s.getItem(TABLE_KEY);
    return s;
  } catch {
    return null;
  }
}

const isRow = (v: unknown): v is HighScoreRow =>
  v !== null && typeof v === "object"
  && typeof (v as HighScoreRow).score === "number"
  && typeof (v as HighScoreRow).at === "number";

/** Highest first; a tie goes to the game that finished sooner in game days. */
export function readScores(): readonly HighScoreRow[] {
  const s = storage();
  if (s === null) return [];
  try {
    const raw = s.getItem(TABLE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isRow).sort((a, b) => b.score - a.score || a.day - b.day);
  } catch {
    return [];
  }
}

export function recordScore(row: HighScoreRow): readonly HighScoreRow[] {
  const next = [...readScores(), row]
    .sort((a, b) => b.score - a.score || a.day - b.day)
    .slice(0, MAX_SCORES);
  const s = storage();
  if (s !== null) {
    try {
      s.setItem(TABLE_KEY, JSON.stringify(next));
    } catch { /* a table that will not fit is not worth failing the end screen over */ }
  }
  return next;
}

export function clearScores(): void {
  const s = storage();
  if (s === null) return;
  try {
    s.removeItem(TABLE_KEY);
  } catch { /* nothing to clear */ }
}
